// bd-deepcontext-b4zw — all create/edit form state and its transitions, lifted out
// of ScheduledJobs.tsx: which modal is open, the form values, the domain-filter
// selection, the inline countries error, the bd-qiwz7 blueprint-country
// provenance ref, and the pre-flight validation.
import { useCallback, useRef, useState } from "react";
import {
  parseBlueprintCountries,
  syncCountriesFromBlueprints,
} from "../blueprintCountrySync";
import {
  EMPTY_FORM_DATA,
  type ScheduledJobFormData,
} from "../scheduledJobFormDefaults";
import type { SelectedDomainInfo } from "../SiteListModal";
import {
  jobToDuplicateFormData,
  jobToFormData,
  validateJobForm,
  type JobFormMode,
} from "./formPayload";
import type { ScheduledJob } from "./types";

export interface JobFormState {
  showCreateForm: boolean;
  showEditForm: boolean;
  showDuplicateForm: boolean;
  isFormOpen: boolean;
  editingJob: ScheduledJob | null;
  duplicateSourceJob: ScheduledJob | null;
  formData: ScheduledJobFormData;
  selectedDomainInfos: SelectedDomainInfo[];
  countriesError: string | null;
  patchFormData: (patch: Partial<ScheduledJobFormData>) => void;
  setCountryCodes: (countryCodes: string[]) => void;
  syncBlueprintCountries: (
    countriesByPromptId: Record<string, string | null | undefined>,
  ) => void;
  applyDomainSelection: (domainInfos: SelectedDomainInfo[]) => void;
  openCreateForm: () => void;
  startEdit: (job: ScheduledJob) => void;
  startDuplicate: (job: ScheduledJob) => void;
  closeCreateModal: () => void;
  finishCreate: () => void;
  closeEditModal: () => void;
  closeDuplicateModal: () => void;
  /** True when the form is invalid; the message is pushed to `onError`. */
  reportInvalid: (mode: JobFormMode) => boolean;
}

export function useJobFormState({
  onError,
}: {
  onError: (message: string) => void;
}): JobFormState {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDuplicateForm, setShowDuplicateForm] = useState(false);
  const [editingJob, setEditingJob] = useState<ScheduledJob | null>(null);
  const [duplicateSourceJob, setDuplicateSourceJob] =
    useState<ScheduledJob | null>(null);
  const [selectedDomainInfos, setSelectedDomainInfos] = useState<
    SelectedDomainInfo[]
  >([]);
  const [countriesError, setCountriesError] = useState<string | null>(null);
  const [formData, setFormData] = useState<ScheduledJobFormData>({
    ...EMPTY_FORM_DATA,
  });
  // bd-deepcontext-qiwz7 — provenance for 'Traffic from' two-way sync: the set
  // of country codes (lowercased) currently contributed by attached blueprints.
  // Countries NOT in this set are treated as manual and preserved across
  // blueprint attach/remove. Reset to [] when a form opens — in edit, the job's
  // already-saved countries are thus treated as manual/pre-existing.
  const blueprintOwnedCountriesRef = useRef<string[]>([]);

  const patchFormData = useCallback(
    (patch: Partial<ScheduledJobFormData>) =>
      setFormData((prev) => ({ ...prev, ...patch })),
    [],
  );

  const setCountryCodes = useCallback((countryCodes: string[]) => {
    setFormData((prev) => ({ ...prev, countryCodes }));
    if (countryCodes.length > 0) setCountriesError(null);
  }, []);

  // bd-deepcontext-qiwz7 — fired by PromptMultiSelect whenever the set of
  // selected blueprints (or their resolved country data) changes. Recomputes
  // 'Traffic from' as: manual countries (preserved) ∪ deduped union of every
  // attached blueprint's countries; drops countries a removed blueprint
  // uniquely contributed. Updates the provenance ref so the next change knows
  // what is safe to remove.
  const syncBlueprintCountries = useCallback(
    (countriesByPromptId: Record<string, string | null | undefined>) => {
      const attachedPromptIds = Object.keys(countriesByPromptId);
      const countriesByBlueprint: Record<string, string[]> = {};
      for (const id of attachedPromptIds) {
        countriesByBlueprint[id] = parseBlueprintCountries(
          countriesByPromptId[id],
        );
      }
      setFormData((prev) => {
        const { countryCodes, blueprintOwned } = syncCountriesFromBlueprints(
          prev.countryCodes,
          blueprintOwnedCountriesRef.current,
          attachedPromptIds,
          countriesByBlueprint,
        );
        blueprintOwnedCountriesRef.current = blueprintOwned;
        if (countryCodes.length > 0) setCountriesError(null);
        // Avoid a needless state churn when nothing actually changed.
        if (
          countryCodes.length === prev.countryCodes.length &&
          countryCodes.every((c, i) => c === prev.countryCodes[i])
        ) {
          return prev;
        }
        return { ...prev, countryCodes };
      });
    },
    [],
  );

  const applyDomainSelection = useCallback(
    (domainInfos: SelectedDomainInfo[]) => {
      setSelectedDomainInfos(domainInfos);
      setFormData((prev) => ({ ...prev, selectedDomainInfos: domainInfos }));
    },
    [],
  );

  const openCreateForm = useCallback(() => {
    // bd-deepcontext-hrq9n — always open the create form blank.
    setFormData({ ...EMPTY_FORM_DATA });
    setSelectedDomainInfos([]);
    setCountriesError(null);
    // bd-deepcontext-qiwz7 — no blueprint owns any country yet.
    blueprintOwnedCountriesRef.current = [];
    setShowCreateForm(true);
  }, []);

  const startEdit = useCallback((job: ScheduledJob) => {
    setEditingJob(job);
    // bd-deepcontext-qiwz7 — the job's already-saved countries are treated as
    // manual/pre-existing (not blueprint-owned), so they survive blueprint
    // attach/remove. Start with no blueprint provenance; the first
    // onSelectedBlueprintCountriesChange (once the cache resolves the attached
    // blueprints) unions in their countries on top.
    blueprintOwnedCountriesRef.current = [];
    const nextForm = jobToFormData(job);
    setSelectedDomainInfos(nextForm.selectedDomainInfos);
    setFormData(nextForm);
    setShowEditForm(true);
  }, []);

  // deepcontext-c919.2 — every field pre-filled from the source job, dates
  // cleared, so the user only has to pick the new active window.
  const startDuplicate = useCallback((job: ScheduledJob) => {
    setDuplicateSourceJob(job);
    blueprintOwnedCountriesRef.current = [];
    const nextForm = jobToDuplicateFormData(job);
    setSelectedDomainInfos(nextForm.selectedDomainInfos);
    setFormData(nextForm);
    setShowDuplicateForm(true);
  }, []);

  const closeCreateModal = useCallback(() => {
    setShowCreateForm(false);
    setCountriesError(null);
  }, []);

  // After a successful create the form is emptied as well as closed, so the next
  // open can never show the saved job's values (bd-deepcontext-hrq9n).
  const finishCreate = useCallback(() => {
    setShowCreateForm(false);
    setSelectedDomainInfos([]);
    setCountriesError(null);
    setFormData({ ...EMPTY_FORM_DATA });
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditForm(false);
    setEditingJob(null);
    setSelectedDomainInfos([]);
    setCountriesError(null);
  }, []);

  const closeDuplicateModal = useCallback(() => {
    setShowDuplicateForm(false);
    setDuplicateSourceJob(null);
    setSelectedDomainInfos([]);
    setCountriesError(null);
  }, []);

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const reportInvalid = useCallback(
    (mode: JobFormMode) => {
      const invalid = validateJobForm(formData, mode);
      if (!invalid) {
        setCountriesError(null);
        return false;
      }
      onErrorRef.current(invalid.message);
      if (invalid.field === "countries") setCountriesError(invalid.message);
      return true;
    },
    [formData],
  );

  return {
    showCreateForm,
    showEditForm,
    showDuplicateForm,
    isFormOpen: showCreateForm || showEditForm || showDuplicateForm,
    editingJob,
    duplicateSourceJob,
    formData,
    selectedDomainInfos,
    countriesError,
    patchFormData,
    setCountryCodes,
    syncBlueprintCountries,
    applyDomainSelection,
    openCreateForm,
    startEdit,
    startDuplicate,
    closeCreateModal,
    finishCreate,
    closeEditModal,
    closeDuplicateModal,
    reportInvalid,
  };
}
